import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import Associate_bs from "../CompanyProfile/Associate_bs";
import { fetchWithAuth, useUser } from "../Context/UserContext";
import { useCompany } from "../Context/CompanyContext";
import geoData from "../Data/GeoCodes";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import API_URL from "../config";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const ProfileHeader = ({
  userData,
  isOwner,
  initialIsEditing,
  initialIsModalOpen,
}) => {
  const { companies, fetchCompanies, handleDissociate } = useCompany();
  const { setUser } = useUser();
  const [isEditing, setIsEditing] = useState(initialIsEditing || false);
  const [isModalOpen, setIsModalOpen] = useState(initialIsModalOpen || false);
  const [isDissociateModalOpen, setIsDissociateModalOpen] = useState(false);
  const [formData, setFormData] = useState(userData || []); // Se inicializa desde userData
  const [errors, setErrors] = useState({});
  const [isLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Aplicar formato solo a nombre y apellido
    const formattedValue =
      name === "nombre" || name === "apellido"
        ? value.charAt(0).toUpperCase() + value.slice(1) // Solo la primera letra en mayúscula
        : value; // Mantener otros valores sin cambios

    setFormData((prevData) => ({
      ...prevData,
      user: {
        ...prevData.user,
        [name]: formattedValue,
      },
    }));
  };

  // Validar el telefono
  const validatePhoneNumber = (celular) => {
    if (!/^\d{9,}$/.test(celular)) {
      return "El número de celular debe tener al menos 9 dígitos numéricos";
    }

    return null; // Si no hay errores, retorna null
  };

  // Guardar cambios del perfil
  const handleSaveChanges = async () => {
    const celularError = validatePhoneNumber(formData.user.celular);
    const correoError = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.user.correo)
      ? "El correo electrónico no es válido"
      : null;

    if (celularError || correoError) {
      setErrors({
        celular: celularError,
        correo: correoError,
      });
      return;
    }

    if (!isOwner) {
      console.error("No tienes permiso para modificar este perfil.");
      return; // Evitar guardar si no es el propietario
    }

    try {
      const response = await fetchWithAuth(`${API_URL}/profile/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData, // Aquí enviamos toda la estructura completa (user, etc.)
        }),
      });

      const data = await response.json();

      if (data.error) {
        setErrors({ general: data.error });
      } else {
        // Actualizar el perfil en el estado global del contexto con setUser
        setUser((prevUser) => ({
          ...prevUser,
          user: { ...formData.user }, // Actualizamos el objeto de usuario con los cambios de formData
          profile: { ...prevUser.profile, ...formData.profile }, // Si hay algún cambio en el perfil
        }));

        setIsEditing(false); // Cerrar el modo de edición
      }
    } catch (error) {
      console.error("Error al actualizar el perfil:", error);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-6 pt-6 sm:flex-row sm:items-start sm:justify-between">
        {/* Información del usuario */}
        <div className="flex items-start gap-4">
          <img
            src={formData.profile?.profileImage || `${process.env.PUBLIC_URL}/fotos/persona.png`}
            alt="Foto de perfil"
            className="h-16 w-16 rounded-full object-cover"
          />
          <div>
            <h2
              className="font-semibold text-foreground"
              title={`${formData.user?.nombre} ${formData.user?.apellido}`}
            >
              {`${formData.user?.nombre} ${formData.user?.apellido}`.length > 30
                ? `${formData.user?.nombre} ${formData.user?.apellido}`.slice(0, 30) + "..."
                : `${formData.user?.nombre} ${formData.user?.apellido}`}
            </h2>
            <p className="text-sm text-muted-foreground" title={formData.user?.correo}>
              {formData.user?.correo.length > 30
                ? formData.user?.correo.slice(0, 30) + "..."
                : formData.user?.correo}
            </p>
            <p
              className="text-sm text-muted-foreground"
              title={`Celular: (${formData.user?.codigo_pais}) - ${formData.user?.celular}`}
            >
              <strong className="text-foreground">Celular:</strong>{" "}
              {`(${formData.user?.codigo_pais}) - ${formData.user?.celular}`.length > 20
                ? `(${formData.user?.codigo_pais}) - ${formData.user?.celular}`.slice(0, 20) + "..."
                : `(${formData.user?.codigo_pais}) - ${formData.user?.celular}`}
            </p>
          </div>

          {isOwner && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Editar perfil"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Información de la empresa asociada */}
        <div>
          {userData?.companyInfo &&
          userData.companyUser &&
          Object.keys(userData.companyUser).length > 0 ? (
            <div className="flex items-start gap-2">
              <div>
                <h3 className="font-semibold text-foreground">
                  <Link
                    to={`/company-profile/${userData.companyInfo.id_company}/${userData.companyInfo.nombre_empresa}`}
                    className="hover:text-primary"
                  >
                    {userData.companyUser?.estado_usuario === "pendiente"
                      ? "Solicitud pendiente"
                      : userData.companyInfo.nombre_empresa?.length > 30
                      ? userData.companyInfo.nombre_empresa.slice(0, 30) + "..."
                      : userData.companyInfo.nombre_empresa || "Registra"}
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Sector:</strong> {userData.companyInfo.sector || "N/A"}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Puesto:</strong>{" "}
                  {userData.companyUser.puesto
                    ? (userData.companyUser.puesto.length > 30
                        ? userData.companyUser.puesto.slice(0, 30) + "..."
                        : userData.companyUser.puesto
                      )
                        .toLowerCase()
                        .replace(/^\w/, (c) => c.toUpperCase())
                    : "N/A"}
                </p>
              </div>

              {isOwner && (
                <button
                  onClick={() => setIsDissociateModalOpen(true)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Desasociar empresa"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            isOwner && (
              <div className="text-right">
                <Button onClick={() => setIsModalOpen(true)}>Registrar Empresa</Button>
                <p className="mt-2 text-sm text-muted-foreground">No estás asociado a ninguna empresa.</p>
              </div>
            )
          )}
        </div>

        {/* Modal de edición del perfil del usuario */}
        <Dialog open={isEditing && isOwner} onOpenChange={(open) => !open && setIsEditing(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Información</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Correo</label>
                <p className="text-sm text-muted-foreground" title={formData.user?.correo}>
                  {formData.user?.correo.length > 30
                    ? formData.user?.correo.slice(0, 30) + "..."
                    : formData.user?.correo}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Nombre</label>
                <Input
                  type="text"
                  name="nombre"
                  value={formData.user?.nombre || ""}
                  onChange={handleInputChange}
                  placeholder="Nombre"
                  maxLength="50"
                  pattern="^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$"
                  title="El nombre solo debe contener letras y espacios"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Apellido</label>
                <Input
                  type="text"
                  name="apellido"
                  value={formData.user?.apellido || ""}
                  onChange={handleInputChange}
                  placeholder="Apellido"
                  maxLength="50"
                  pattern="^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$"
                  title="El apellido solo debe contener letras y espacios"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Celular</label>
                <div className="flex gap-2">
                  <select
                    name="codigo_pais"
                    value={formData.user?.codigo_pais}
                    onChange={handleInputChange}
                    className="rounded-md border border-border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {geoData.map((geo) => (
                      <option key={geo.code} value={geo.dialCode}>
                        {geo.code} (+{geo.dialCode})
                      </option>
                    ))}
                  </select>
                  <Input
                    type="text"
                    name="celular"
                    value={formData.user?.celular}
                    onChange={handleInputChange}
                    maxLength="15"
                    pattern="^\d{9,15}$"
                    title="El número de celular debe tener entre 9 y 15 dígitos numéricos"
                    placeholder="Número de celular"
                  />
                </div>
              </div>
              {errors.general && <p className="text-sm text-destructive">{errors.general}</p>}
            </div>
            <DialogFooter>
              <Button type="button" onClick={handleSaveChanges}>
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal para eliminar o disociar empresa */}
        <Dialog open={isDissociateModalOpen} onOpenChange={setIsDissociateModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminar asociación</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">¿Realmente te quieres ir de esta empresa?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDissociateModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleDissociate(companies.company.id_company); // Usamos la función del contexto
                  setIsDissociateModalOpen(false);
                }}
              >
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal para registrar empresa */}
        <Dialog open={isModalOpen} onOpenChange={(open) => !open && setIsModalOpen(false)}>
          <DialogContent>
            <Associate_bs
              handleClose={handleClose}
              setIsModalOpen={setIsModalOpen}
              onCompanyRegistered={async (newCompany) => {
                fetchCompanies();

                setUser((prevUser) => ({
                  ...prevUser,
                  companyUser: newCompany,
                }));

                setIsModalOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ProfileHeader;
